import { Grid } from "@mui/material";
import { useFieldDependencies } from "@platform/hooks";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { Typography } from "@platform/design-system";

import {
  ArrayStepper,
  ArrayStepperConfig,
  FieldConfig,
  FormElementConfig,
  SectionConfig,
} from "./FormComponents";
import { FieldElement } from "./FieldElement";
import { isArrayStepperConfig, isSectionConfig } from "./predicates";

export const SectionRenderer = <
  TCustomType extends string,
  Values extends FieldValues,
>({
  element: section,
  methods,
}: {
  element: SectionConfig<TCustomType>;
  methods: UseFormReturn<Values>;
}): React.JSX.Element | null => {
  const formState = useWatch({ control: methods.control });
  const { isVisible } = useFieldDependencies(section, formState);

  if (!isVisible) {
    return null;
  }

  return (
    <Grid container {...section.grid} spacing={2}>
      {section.label && (
        <Grid container size={12}>
          <Typography variant="header-medium" py={2} sx={{ fontWeight: 400 }}>
            {section.label}
          </Typography>
        </Grid>
      )}
      {section.children.map((child, index) => {
        let key: string;
        if ((child as SectionConfig<TCustomType>).type === "section") {
          const sectionChild = child as SectionConfig<TCustomType>;
          key = `form-section-${sectionChild.label || index}`;
        } else if (isArrayStepperConfig(child)) {
          key = `form-array-stepper-${(child as { name: string }).name}`;
        } else {
          const fieldChild = child as FieldConfig<TCustomType>;
          key = `form-control-${fieldChild.type}-${fieldChild.name || index}`;
        }
        return (
          <FormElementRenderer key={key} element={child} methods={methods} />
        );
      })}
    </Grid>
  );
};

export const ArrayStepperRenderer = <
  TCustomType extends string,
  Values extends FieldValues,
>({
  element,
  methods,
}: {
  element: ArrayStepperConfig<TCustomType>;
  methods: UseFormReturn<Values>;
}): React.JSX.Element => (
  <Grid
    {...element.grid}
    key={element.name}
    data-testid={`ds-form-array-stepper-${element.name}`}
  >
    <ArrayStepper<TCustomType, Values> field={element} methods={methods} />
  </Grid>
);

export const FormElementRenderer = <
  TCustomType extends string,
  Values extends FieldValues,
>({
  element,
  methods,
}: {
  element: FormElementConfig<TCustomType>;
  methods: UseFormReturn<Values>;
}): React.JSX.Element | null => {
  if (isSectionConfig(element)) {
    return <SectionRenderer element={element} methods={methods} />;
  }

  if (isArrayStepperConfig(element)) {
    return <ArrayStepperRenderer element={element} methods={methods} />;
  }

  return <FieldElement field={element} methods={methods} />;
};
